import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Profiles, type DirectoryEntry } from '@/integrations/api';

interface ParticipantsSelectProps {
  /** Selected participants, as employee initials. */
  value: string[];
  onChange: (initials: string[]) => void;
}

/**
 * Multi-select of employees for an expense's participants. Employees are shown by their
 * initials (the email's local part); the chosen ones are stored as a comma-separated
 * string by the caller. The current user is pre-selected by the caller but can be removed.
 *
 * <p>Responsive: on desktop the list is an anchored Popover; on mobile it is a bottom-sheet
 * Drawer with larger touch targets and a tall scrollable list, so the on-screen keyboard
 * (opened by the search field) doesn't cover the options and they stay easy to tap.
 */
export function ParticipantsSelect({ value, onChange }: ParticipantsSelectProps) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<DirectoryEntry[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    let active = true;
    Profiles.directory()
      .then((list) => { if (active) setPeople(list); })
      .catch(() => { /* directory unavailable — picker just shows nothing */ });
    return () => { active = false; };
  }, []);

  const toggle = (initials: string) =>
    onChange(value.includes(initials) ? value.filter((i) => i !== initials) : [...value, initials]);
  const remove = (initials: string) => onChange(value.filter((i) => i !== initials));

  const trigger = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className="w-full justify-between font-normal h-auto min-h-10 py-2"
    >
      {value.length === 0 ? (
        <span className="text-muted-foreground">Select participants</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {value.map((initials) => (
            <Badge key={initials} variant="secondary" className="gap-1">
              {initials}
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Remove ${initials}`}
                onClick={(e) => { e.stopPropagation(); remove(initials); }}
                className="hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </span>
            </Badge>
          ))}
        </div>
      )}
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  // Shared search + list. `mobile` bumps the item size and gives the list more height so it
  // scrolls above the keyboard. Selecting toggles without closing (it's a multi-select).
  const picker = (mobile: boolean) => (
    <Command>
      <CommandInput placeholder="Search employees..." />
      <CommandList className={mobile ? 'max-h-[55vh]' : undefined}>
        <CommandEmpty>No employees found.</CommandEmpty>
        <CommandGroup>
          {people.map((p) => {
            const selected = value.includes(p.initials);
            return (
              <CommandItem
                key={p.user_id}
                value={`${p.initials} ${p.name ?? ''}`}
                onSelect={() => toggle(p.initials)}
                className={mobile ? 'py-3 text-base' : undefined}
              >
                <Check className={cn('mr-2 h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
                <span className="font-medium">{p.initials}</span>
                {p.name && <span className="ml-2 truncate text-sm text-muted-foreground">{p.name}</span>}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Select participants</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6">{picker(true)}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {picker(false)}
      </PopoverContent>
    </Popover>
  );
}
